import type { ContentRatingDetails } from '@communities/community/domain';
import { ExternalLink } from '@soteria/design-system-ve';

import * as styles from './ContentRatingDisplay.css';

interface RichContentRatingProps {
  contentRating: ContentRatingDetails;
  genres?: string[];
  ratingIconUrl?: string;
  ratingUrl?: string;
  className?: string;
}

/**
 * RichContentRating (refactored)
 *
 * Changes (per regression analysis and updated requirements):
 * - Remove badge concept entirely (no square value block)
 * - Use human-friendly description as the primary visible label
 * - Retain optional rating icon (official system artwork) if provided
 * - Exclude interactiveElements (intentional omission to reduce noise)
 * - Show descriptors (comma separated) below rating when present
 * - Show genres separated by a single divider ONLY when both descriptors and genres exist
 * - If no descriptors, genres render without a preceding divider
 * - Link precedence: prefer system (contentRating.ratingUrl) then fallback to prop
 * - Accessibility: descriptive aria-label uses rating system + description
 */

/* Extracted small presentational helpers to reduce main component complexity */
function RatingIcon({ ratingIconUrl, ratingSystem, description }: { ratingIconUrl?: string; ratingSystem: string; description: string }) {
  if (!ratingIconUrl) return null;

  return <img alt={`${ratingSystem} ${description} rating`} className={styles.ratingBadge} height={48} loading="lazy" src={ratingIconUrl} width={48} />;
}

function RatingLabel({ effectiveRatingUrl, ratingSystem, description }: { effectiveRatingUrl?: string; ratingSystem: string; description: string }) {
  if (!effectiveRatingUrl) {
    return (
      <span aria-label={`${ratingSystem} ${description} rating`} className={styles.ratingText}>
        {description}
      </span>
    );
  }

  return (
    <ExternalLink
      aria-label={`${ratingSystem} ${description} rating information (opens in new tab)`}
      className={styles.ratingLink}
      href={effectiveRatingUrl}
      variant="standard"
    >
      {description}
    </ExternalLink>
  );
}

function GenresSection({ genres, hasDescriptors }: { genres?: string[]; hasDescriptors: boolean }) {
  if (!genres || genres.length === 0) return null;

  return (
    <>
      {hasDescriptors ? <div className={styles.divider} /> : null}
      <div className={styles.genresText}>{genres.join(', ')}</div>
    </>
  );
}

export function RichContentRating({ contentRating, genres, ratingIconUrl, ratingUrl, className }: RichContentRatingProps) {
  if (!contentRating) return null;

  const { ratingSystem, description, descriptors, ratingUrl: systemRatingUrl } = contentRating;
  const effectiveRatingUrl = systemRatingUrl || ratingUrl;

  const descriptorLine = descriptors && descriptors.length > 0 ? descriptors.join(', ') : '';
  const hasDescriptors = Boolean(descriptorLine);

  return (
    <div className={className ? `${styles.container} ${className}` : styles.container}>
      <div className={styles.ratingRow}>
        <RatingIcon description={description} ratingIconUrl={ratingIconUrl} ratingSystem={ratingSystem} />
        <RatingLabel description={description} effectiveRatingUrl={effectiveRatingUrl} ratingSystem={ratingSystem} />
      </div>
      {hasDescriptors ? <div className={styles.descriptorsText}>{descriptorLine}</div> : null}
      <GenresSection genres={genres} hasDescriptors={hasDescriptors} />
    </div>
  );
}
